import { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/motion";
import { useLang } from "@/i18n";
import { UNITS } from "@/data/site";

const AUTO = 6;

// Reviews pinned in the panel ("In home"); without pins, a few short
// five-star ones taken in turn from each apartment. One at a time, moved by
// the visitor (arrows or swipe): no autoplay, nobody gets rushed mid-sentence.
function pick(lang) {
  const all = UNITS.map((u) => u.reviews.map((r) => ({ unit: u.name, pinned: r.featured, rating: r.rating, text: r.text?.[lang] || r.text?.it || "" })));
  const pinned = all.flat().filter((r) => r.pinned && r.text);
  if (pinned.length) return pinned;
  const good = all.map((list) => list.filter((r) => r.rating === 5 && r.text.length >= 70 && r.text.length <= 220));
  const out = [];
  for (let i = 0; out.length < AUTO && good.some((l) => l[i]); i++) good.forEach((l) => l[i] && out.length < AUTO && out.push(l[i]));
  return out;
}

export default function HomeReviews() {
  const { t, lang } = useLang();
  const reviews = pick(lang);
  const track = useRef(null);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const el = track.current;
    if (!el) return undefined;
    const on = () => setIndex(Math.round(el.scrollLeft / el.clientWidth));
    el.addEventListener("scroll", on, { passive: true });
    return () => el.removeEventListener("scroll", on);
  }, []);

  if (!reviews.length) return null;
  const go = (i) => track.current.scrollTo({ left: i * track.current.clientWidth, behavior: "smooth" });
  const arrow = "inline-flex h-11 w-11 items-center justify-center rounded-full border border-ink/20 text-ink hover:border-terracotta hover:text-terracotta transition-colors duration-200 disabled:opacity-30 disabled:cursor-default";

  return (
    <Reveal>
      <section className="max-w-4xl mx-auto px-6 pb-20 md:pb-32 text-center" aria-roledescription="carousel" aria-label={t.homeUnits.reviews} data-testid="home-review">
        <span aria-hidden="true" className="block font-display text-7xl md:text-8xl leading-[0.6] text-terracotta/50">“</span>
        <div ref={track} className="mt-4 flex overflow-x-auto snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="home-review-track">
          {reviews.map((r, i) => (
            <figure key={i} className="snap-start shrink-0 basis-full px-1" aria-roledescription="slide" aria-label={`${i + 1} / ${reviews.length}`}>
              <blockquote className="font-display italic text-2xl md:text-4xl leading-snug text-ink text-balance">{r.text}</blockquote>
              <figcaption className="mt-5 text-xs uppercase tracking-[0.2em] text-stone">
                <span className="text-terracotta" aria-label={`${r.rating}/5`}>{"★".repeat(r.rating)}</span> · {t.homeUnits.verified} · {r.unit}
              </figcaption>
            </figure>
          ))}
        </div>
        {reviews.length > 1 && (
          <div className="mt-8 flex items-center justify-center gap-4">
            <button type="button" onClick={() => go(index - 1)} disabled={index === 0} aria-label={t.homeUnits.prevReview} className={arrow}>
              <ArrowLeft className="h-5 w-5" aria-hidden="true" />
            </button>
            <button type="button" onClick={() => go(index + 1)} disabled={index === reviews.length - 1} aria-label={t.homeUnits.nextReview} className={arrow}>
              <ArrowRight className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        )}
      </section>
    </Reveal>
  );
}
